import React, { useMemo, useCallback } from 'react';
import { flatThreadToTree } from '../../helpers';
import RecursiveComment from '../RecursiveComment';
import { useScrollToLatestComment } from './useScrollToLatestComment';
import { useParentIndicators } from './useParentIndicators';
import { useReplyState } from './useReplyState';
import { useThreadChanged } from './useThreadChanged';
const ThreadStack = ({ items, maxDepth, maxCommentsShown, lastInsertedCommentId, parentComment, parentCommentRef, commentableItemId, commentableTypeId, onOpenCommentModal, streamId, parentId, canPostComment, allowSelfInit, onThreadUpdated, commentComponent, commentClassName, commentWrapperClassName, viewRepliesClassName, enableForumThreadRootlessThreadMode, maxDepthOverride, replyIndentModifier, className, showOnlyLastInsertedComment })=>{
    const renderItems = useMemo(()=>{
        if (showOnlyLastInsertedComment) {
            return flatThreadToTree(items.filter((comment)=>comment.commentId === lastInsertedCommentId), maxDepth);
        }
        const limitedItems = maxCommentsShown ? items.slice(0, maxCommentsShown) : items;
        return flatThreadToTree(limitedItems, maxDepth);
    }, [
        items,
        maxCommentsShown,
        maxDepth,
        showOnlyLastInsertedComment,
        lastInsertedCommentId
    ]);
    const onThreadUpdatedCallback = useCallback((result)=>onThreadUpdated(streamId, result), [
        onThreadUpdated,
        streamId
    ]);
    useThreadChanged(commentableItemId, commentableTypeId, parentId, allowSelfInit, maxDepthOverride);
    const { onEditingStateChange, isReplyingToComment } = useReplyState();
    const { setLastComment } = useScrollToLatestComment(lastInsertedCommentId);
    const { setVerticalIndicatorRef, setCommentRef: setCommentRefForIndicators, onCommentMouseOver } = useParentIndicators(parentComment, parentCommentRef);
    const setCommentRef = useCallback((commentId, element)=>{
        setCommentRefForIndicators(commentId, element);
        if (lastInsertedCommentId && lastInsertedCommentId === commentId) {
            setLastComment(element);
        }
    }, [
        lastInsertedCommentId,
        setLastComment,
        setCommentRefForIndicators
    ]);
    if (renderItems.length === 0) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: className
    }, renderItems.map((item)=>/*#__PURE__*/ React.createElement(RecursiveComment, {
            key: `comment-wrap-${item.commentId}`,
            index: item.index,
            commentableItemId: commentableItemId,
            commentableTypeId: commentableTypeId,
            canPostComment: canPostComment,
            streamId: streamId,
            comment: item,
            maxDepth: maxDepth,
            commentComponent: commentComponent,
            commentClassName: commentClassName,
            commentWrapperClassName: commentWrapperClassName,
            viewRepliesClassName: viewRepliesClassName,
            enableForumThreadRootlessThreadMode: enableForumThreadRootlessThreadMode,
            replyIndentModifier: replyIndentModifier,
            isReplyingToParent: isReplyingToComment(item.parentId),
            setCommentRef: setCommentRef,
            onCommentMouseOver: onCommentMouseOver,
            setVerticalIndicatorRef: setVerticalIndicatorRef,
            onEditingStateChange: onEditingStateChange,
            onOpenCommentModal: onOpenCommentModal,
            onThreadUpdated: onThreadUpdatedCallback
        })));
};
ThreadStack.displayName = 'ThreadStack';
export default ThreadStack;
